import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

// Redux + MUI + etc
import { ThemeProvider } from "@mui/material/styles";
import { configureStore } from "@reduxjs/toolkit";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";

// === Auth (digital-sizer JWT, replaces Stytch) ===
import { AuthProvider } from "./auth/AuthProvider";

// Your own modules
import AuthenticatedDashboard from "./AuthenticatedDashboard";
import AdminLayout from "./AuthenticatedDashboard/AdminLayout";
import SizersLayout from "./AuthenticatedDashboard/SizersLayout";
import BannedAppraisersScreen from "./screens/BannedAppraisersScreen/BannedAppraisersScreen";
import AcquisitionsPipelineScreen from "./screens/AcquisitionsPipelineScreen/AcquisitionsPipelineScreen";
import AcquisitionsWireQueueScreen from "./screens/AcquisitionsWireQueueScreen/AcquisitionsWireQueueScreen";
import { PipelineProvider } from "./pipelines/PipelineContext";
import { PIPELINE_KEYS } from "./pipelines/pipelineConfig";
import ProtectedRoute from "./authentication/ProtectedRoute";
import generateMuiThemeDefaults from "./functions/generateMuiThemeDefaults";
import "./index.css";
import rootReducer from "./redux/reducers";
import LoginScreen from "./screens/LoginScreen";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";

// AG Grid
import { LicenseManager } from "ag-grid-enterprise";
import LoanDrilldownScreen from "./screens/LoanDrilldownScreen/LoanDrilldownScreen";
import LoanOfficerPipelineScreen from "./screens/LoanOfficerPipelineScreen";
import LoanQuickIntakeFormScreen from "./screens/LoanQuickIntakeFormScreen";
import PreClosePipelineScreen from "./screens/PipelineTableScreen/PreClosePipelineScreen";
import ClosingPipelineScreen from "./screens/PipelineTableScreen/ClosingPipelineScreen";
import ClosedLoansPipelineScreen from "./screens/PipelineTableScreen/ClosedLoansPipelineScreen";
import FullPipelineScreen from "./screens/PipelineTableScreen/FullPipelineScreen";
import ClosingCalendarScreen from "./screens/ClosingCalendarScreen/ClosingCalendarScreen";
// Legacy loan-closings calendar — reachable by direct URL only (not in nav) in
// case we want to switch back. The /loans/closing/calendar slot now renders the
// closing-team coverage calendar (ClosingCalendarScreen).
import LoanClosingsCalendarScreen from "./screens/LoanClosingsCalendarScreen/LoanClosingsCalendarScreen";
import PostClosePipelineScreen from "./screens/PipelineTableScreen/PostClosePipelineScreen";
import LoanLockRequestsPipelineScreen from "./screens/PipelineTableScreen/LoanLockRequestsPipelineScreen";
import LockQueueScreen from "./screens/LockQueueScreen/LockQueueScreen";
import FundingsScreen from "./screens/FundingsScreen/FundingsScreen";
import AppraisalQueueScreen from "./screens/AppraisalQueueScreen/AppraisalQueueScreen";
import TaskListManagerScreen from "./screens/TaskListManagerScreen/TaskListManagerScreen";
import CorrespondentsScreen from "./screens/CorrespondentsScreen/CorrespondentsScreen";
import GuidelinesScreen from "./screens/GuidelinesScreen/GuidelinesScreen";
import GridsScreen from "./screens/GridsScreen/GridsScreen";
import RulesScreen from "./screens/RulesScreen/RulesScreen";
import PricingScreen from "./screens/PricingScreen/PricingScreen";
import UsersScreen from "./screens/UsersScreen/UsersScreen";
import UserActivityScreen from "./screens/UsersScreen/UserActivity/UserActivityScreen";
import ProfileScreen from "./screens/ProfileScreen/ProfileScreen";
import SupportScreen from "./screens/SupportScreen/SupportScreen";
import ChangelogScreen from "./screens/ChangelogScreen/ChangelogScreen";
import RequestBoardScreen from "./screens/RequestBoardScreen/RequestBoardScreen";
import NotificationsScreen from "./screens/NotificationsScreen/NotificationsScreen";
import EndpointTestScreen from "./screens/EndpointTestScreen/EndpointTestScreen";

// Admin section — one screen per eastview-os `/admin/*` surface.
import AdminGuidelinesScreen from "./screens/admin/AdminGuidelinesScreen/AdminGuidelinesScreen";
import AdminPricingScreen from "./screens/admin/AdminPricingScreen/AdminPricingScreen";
import AdminMarketDataScreen from "./screens/admin/AdminMarketDataScreen/AdminMarketDataScreen";
import AdminSizersScreen from "./screens/admin/AdminSizersScreen/AdminSizersScreen";
import AdminSchemasScreen from "./screens/admin/AdminSchemasScreen/AdminSchemasScreen";
import AdminExtractorsScreen from "./screens/admin/AdminExtractorsScreen/AdminExtractorsScreen";
import AdminGraphSubscriptionsScreen from "./screens/admin/AdminGraphSubscriptionsScreen/AdminGraphSubscriptionsScreen";

import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { useEffect } from "react";
import { useEnv } from "./hooks/useEnv";
import { installConsoleCapture } from "./utils/consoleCapture";

// Start recording console output as early as possible so diagnostics (e.g. the
// SlowRequestPrompt support ticket) can include recent logs/errors.
installConsoleCapture();

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_KEY);

// Create Redux store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== "production",
});

// Carries the counterparty through from the retired /admin/dscr-pricing/:cpId
// path so old links land on the same sheet rather than the default one.
const LegacyDscrPricingRedirect = () => {
  const { cpId } = useParams();
  return <Navigate to={`/admin/pricing/dscr/${cpId}`} replace />;
};

// The sizers moved from /admin/sizers/* to /sizers/* (2026-10-15). Carries the
// tab AND the query string (?scenario=, ?id=, ?entry=quick) so saved scenario
// links and bookmarks land on the same thing.
const LegacyAdminSizersRedirect = () => {
  const { tab } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/sizers/${tab || "scenarios"}${search}`} replace />;
};

// ----------------------
// ContentRenderer FIRST
// ----------------------
const ContentRenderer = () => {
  const { displayName } = useEnv();

  useEffect(() => {
    document.title = `${displayName} | Portal`;
    const faviconPath = "%PUBLIC_URL%/eastview-favicon.png";

    const head = document.querySelector("head");
    if (!head) return;

    let faviconLink =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    faviconLink.rel = "icon";
    const publicUrl = process.env.PUBLIC_URL || "";
    faviconLink.href = faviconPath.replace("%PUBLIC_URL%", publicUrl);

    if (!faviconLink.parentNode) {
      head.appendChild(faviconLink);
    }
  }, [displayName]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />

      {/* Private/Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AuthenticatedDashboard>
              <Routes>
                <Route path="/loans" element={<LoanOfficerPipelineScreen />} />
                <Route
                  path="/loans/full-pipeline/*"
                  element={<FullPipelineScreen />}
                />
                <Route
                  path="/loans/pre-close/*"
                  element={<PreClosePipelineScreen />}
                />
                <Route
                  path="/loans/closing/calendar"
                  element={<ClosingCalendarScreen />}
                />
                {/* Hidden: legacy loan-closings calendar (direct URL only). */}
                <Route
                  path="/loans/closing/closings-calendar"
                  element={<LoanClosingsCalendarScreen />}
                />
                <Route
                  path="/loans/closing/*"
                  element={<ClosingPipelineScreen />}
                />
                <Route
                  path="/loans/closed/*"
                  element={<ClosedLoansPipelineScreen />}
                />
                <Route
                  path="/loans/post-close/*"
                  element={<PostClosePipelineScreen />}
                />
                <Route
                  path="/loans/loan-lock-requests/*"
                  element={<LoanLockRequestsPipelineScreen />}
                />
                <Route
                  path="/loans/:name?/*"
                  element={<LoanOfficerPipelineScreen />}
                />
                <Route
                  path="/loan/create"
                  element={<LoanQuickIntakeFormScreen />}
                />
                <Route
                  path="/loan/create/:type/*"
                  element={<LoanQuickIntakeFormScreen />}
                />
                <Route path="/loan/:id/*" element={<LoanDrilldownScreen />} />
                {/* Acquisitions (postclose) — the SAME drilldown, re-pointed at
                    /loan_acquisitions/* by PipelineProvider. See
                    src/pipelines/pipelineConfig.js. */}
                <Route
                  path="/acquisitions"
                  element={
                    <PipelineProvider pipeline={PIPELINE_KEYS.ACQUISITIONS}>
                      <AcquisitionsPipelineScreen />
                    </PipelineProvider>
                  }
                />
                {/* Create an ACQUISITION loan — the same intake form, wrapped in
                    the acquisitions pipeline so createLoan posts to
                    /loan_acquisitions/create_loan and it lands on /acquisition/{id}. */}
                <Route
                  path="/acquisition/create"
                  element={
                    <PipelineProvider pipeline={PIPELINE_KEYS.ACQUISITIONS}>
                      <LoanQuickIntakeFormScreen />
                    </PipelineProvider>
                  }
                />
                <Route
                  path="/acquisition/create/:type/*"
                  element={
                    <PipelineProvider pipeline={PIPELINE_KEYS.ACQUISITIONS}>
                      <LoanQuickIntakeFormScreen />
                    </PipelineProvider>
                  }
                />
                <Route
                  path="/acquisition/:id/*"
                  element={
                    <PipelineProvider pipeline={PIPELINE_KEYS.ACQUISITIONS}>
                      <LoanDrilldownScreen />
                    </PipelineProvider>
                  }
                />
                <Route
                  path="/acquisitions-wire-queue"
                  element={
                    <PipelineProvider pipeline={PIPELINE_KEYS.ACQUISITIONS}>
                      <AcquisitionsWireQueueScreen />
                    </PipelineProvider>
                  }
                />
                <Route path="/lock-queue/*" element={<LockQueueScreen />} />
                {/* Capital Markets fundings: forward view, funding outlook,
                    funding tapes (closing-readiness.md §5, funding-tape-prep.md). */}
                <Route path="/fundings/*" element={<FundingsScreen />} />
                <Route
                  path="/appraisal-queue/*"
                  element={<AppraisalQueueScreen />}
                />
                <Route
                  path="/correspondents/*"
                  element={<CorrespondentsScreen />}
                />
                {/* Admin — the eastview-os `/admin/*` surfaces. All share a
                    sub-nav tab switcher (AdminLayout) matching the header
                    "Admin" dropdown destinations (ADMIN_NAV_ITEMS). */}
                <Route element={<AdminLayout />}>
                  <Route
                    path="/admin/guidelines"
                    element={<AdminGuidelinesScreen />}
                  />
                  <Route
                    path="/admin/guidelines/:slug"
                    element={<AdminGuidelinesScreen />}
                  />
                  {/* One consolidated pricing screen; `tab` is dscr | rtl |
                      config and `cpId` is the DSCR counterparty. */}
                  <Route
                    path="/admin/pricing"
                    element={<Navigate to="/admin/pricing/dscr" replace />}
                  />
                  <Route
                    path="/admin/pricing/:tab"
                    element={<AdminPricingScreen />}
                  />
                  <Route
                    path="/admin/pricing/:tab/:cpId"
                    element={<AdminPricingScreen />}
                  />
                  {/* Superseded by /admin/pricing — kept so existing links and
                      bookmarks still land somewhere useful. */}
                  <Route
                    path="/admin/dscr-pricing"
                    element={<Navigate to="/admin/pricing/dscr" replace />}
                  />
                  <Route
                    path="/admin/dscr-pricing/:cpId"
                    element={<LegacyDscrPricingRedirect />}
                  />
                  <Route
                    path="/admin/rtl-pricing"
                    element={<Navigate to="/admin/pricing/rtl" replace />}
                  />
                  <Route
                    path="/admin/pricing-config"
                    element={<Navigate to="/admin/pricing/config" replace />}
                  />
                  <Route
                    path="/admin/market-data"
                    element={<AdminMarketDataScreen />}
                  />
                  <Route path="/admin/schemas" element={<AdminSchemasScreen />} />
                  <Route
                    path="/admin/extractors"
                    element={<AdminExtractorsScreen />}
                  />
                  <Route
                    path="/admin/graph-subscriptions"
                    element={<AdminGraphSubscriptionsScreen />}
                  />
                  <Route
                    path="/admin/banned-appraisers"
                    element={<BannedAppraisersScreen />}
                  />
                  {/* Sizers moved out to /sizers/* (below) — these keep old
                      links, bookmarks and saved scenario URLs working. */}
                  <Route
                    path="/admin/sizers/:tab"
                    element={<LegacyAdminSizersRedirect />}
                  />
                  <Route
                    path="/admin/sizers"
                    element={<LegacyAdminSizersRedirect />}
                  />
                  {/* Bare /admin lands on the first tab. */}
                  <Route
                    path="/admin"
                    element={<Navigate to="/admin/guidelines" replace />}
                  />
                </Route>

                {/* Sizers — /sizers/{scenarios,rtl,guc,dscr,max-leverage}.
                    Own section (header "Sizers" dropdown), gated in
                    SizersLayout to admins + the "Sizer Visibility" group.
                    The DSCR tab carries both entry depths on one route
                    (`?entry=quick`); the retired /quick-entry tab redirects
                    onto it. Bare /sizers lands on Priced Scenarios — the read
                    side is the section landing page: it says what has already
                    been priced, and every sizer is one click from it. */}
                <Route element={<SizersLayout />}>
                  <Route path="/sizers/:tab" element={<AdminSizersScreen />} />
                  <Route
                    path="/sizers"
                    element={<Navigate to="/sizers/scenarios" replace />}
                  />
                </Route>

                {/* Descoped from the Admin nav (see adminNav.js): digital_sizer
                    ports whose backend routes eastview-os never exposed. Left
                    mounted for direct-URL access pending removal. */}
                <Route path="/pricing" element={<PricingScreen />} />
                <Route path="/grids" element={<GridsScreen />} />
                <Route path="/rules" element={<RulesScreen />} />
                {/* Moved into the Sizers section; kept so old links resolve. */}
                <Route
                  path="/sizer"
                  element={<Navigate to="/sizers/rtl" replace />}
                />
                <Route
                  path="/dscr"
                  element={<Navigate to="/sizers/dscr" replace />}
                />
                <Route path="/task-lists" element={<TaskListManagerScreen />} />
                <Route path="/guidelines" element={<GuidelinesScreen />} />
                <Route path="/guidelines/:slug" element={<GuidelinesScreen />} />
                {/* Back in the Admin nav under /admin/banned-appraisers; this
                    keeps the old direct links working. */}
                <Route
                  path="/banned-appraisers"
                  element={<Navigate to="/admin/banned-appraisers" replace />}
                />
                <Route path="/users" element={<UsersScreen />} />
                {/* The Users tabs are URLs so the header dropdown can link to
                    one; bare /users falls through to the first tab. */}
                <Route path="/users/:tab" element={<UsersScreen />} />
                {/* Standalone home for the same view, for group members who
                    can't see the Users screen. Only one of the two is ever
                    advertised to a given person — see AuthenticatedDashboard. */}
                <Route path="/user-activity" element={<UserActivityScreen />} />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route
                  path="/notifications"
                  element={<NotificationsScreen />}
                />
                <Route path="/support/*" element={<SupportScreen />} />
                <Route path="/changelog/*" element={<ChangelogScreen />} />
                {/* Request Board (mockup) — super admins only; the screen
                    redirects others to /support. */}
                <Route path="/request-board" element={<RequestBoardScreen />} />
                {/* Dev-only API endpoint tester — not mounted in production builds. */}
                {process.env.NODE_ENV === "development" && (
                  <Route
                    path="/endpoint-test"
                    element={<EndpointTestScreen />}
                  />
                )}
                <Route path="*" element={<Navigate to="/loans" />} />
              </Routes>
            </AuthenticatedDashboard>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// ----------------------
// Mount the app
// ----------------------
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <BrowserRouter>
      <SnackbarProvider maxSnack={5}>
        <ThemeProvider theme={generateMuiThemeDefaults()}>
          <AuthProvider>
            <ContentRenderer />
          </AuthProvider>
        </ThemeProvider>
      </SnackbarProvider>
    </BrowserRouter>
  </Provider>,
);
